import { Component, Input, OnInit, Renderer2, ViewChild, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { DynamicComponentDirective } from '../../../directives/dynamic-component.directive';
import * as Models from '../../../models/models-index';
import { ElementComponent } from '../../../models/models-index';
import { PanelElementResolverService } from '../panel-element-resolver.service';

@Component({
  selector: 'ghost-panel-full-width',
  templateUrl: './ghost-panel-full-width.component.html',
  styleUrls: ['./ghost-panel-full-width.component.scss']
})
export class GhostPanelFullWidthComponent implements OnInit, Models.PanelComponent {

  @ViewChild(DynamicComponentDirective, { static: true }) dynamicComponent!: DynamicComponentDirective;
  @Input() panelConfig: Models.Panel;
  @Input() rowPanelId: string;
  @Input() translationService?: Models.ITranslationService;
  @Input() viewContext?: Models.ViewContext;

  panelTitle: string;

  private _dataSets: Models.DataSet[] = [];

  private componentRefs: ElementComponent[] = [];

  @Input()
  set dataSets(value: Models.DataSet[]) {
    this._dataSets = value;
    if (this._dataSets?.length && this.panelConfig) {
      // Datasets are now available, update the components
      this.updateComponentsWithDataSets();
      return;
    }

    // If datasets are not available, start loading animation
    this.componentRefs.forEach(component => {
      component.dataSets = null;
    });
  }

  get dataSets(): Models.DataSet[] {
    return this._dataSets;
  }

  // NEW: Property to hold the perspective-selected configuration.
  selectedConfiguration: Models.PanelConfiguration;

  // NEW: Getter to filter panel configurations based on the selected perspective.
  get filteredPanelConfigs(): Models.PanelConfiguration[] {
    const perspective = this.viewContext?.selectedPerspective;
    const panelSelector = perspective?.selectors.find(s => s.type === 'Panel' && s.path === this.panelConfig.name);
    let configs = this.panelConfig.configurations;
    if (panelSelector?.items?.length) {
      configs = panelSelector.items
        .map(item => configs.find(c => c.name === item))
        .filter(cfg => !!cfg);
    }
    return configs;
  }

  // NEW: Select the default configuration based on the perspective.
  renderDefaultElement(): void {
    const perspective = this.viewContext?.selectedPerspective;
    const panelSelector = perspective?.selectors.find(s => s.type === 'Panel' && s.path === this.panelConfig.name);
    const configs = this.filteredPanelConfigs;
    if (!configs?.length) {
      this.selectedConfiguration = this.panelConfig.configurations[0];
      return;
    }
    const config = panelSelector?.defaultItem
      ? (configs.find(c => c.name === panelSelector.defaultItem) ?? configs[0])
      : configs[0];
    this.selectedConfiguration = config;
  }

  constructor(
    private resolverService: PanelElementResolverService,
    private renderer2: Renderer2,
    private router: Router
  ) { }

  ngOnInit(): void {
    // NEW: Apply perspective-based configuration before generating elements.
    this.renderDefaultElement();
    // Generate elements immediately
    this.generateElements();
  }

  generateElements() {
    if (!this.panelConfig) {
      // Prevent element generation if panelConfig is not available
      return;
    }

    // Use the selected configuration if available, otherwise fallback.
    const config = this.selectedConfiguration || this.panelConfig.configurations[0];

    const viewContainerRef = this.dynamicComponent.viewContainerRef;
    viewContainerRef.clear();
    this.componentRefs = []; // Clear any existing component references

    config.elements.forEach(element => {
      // Initially, dataset might not be available
      const componentRef = this.resolverService.resolveElementComponent(viewContainerRef, element.type);
      componentRef.instance.settings = element.settings;
      componentRef.instance.viewContext = this.viewContext;
      this.renderer2.addClass(componentRef.location.nativeElement, 'ghost-panel-element');
      this.renderer2.setAttribute(componentRef.location.nativeElement, 'style', `width:100%; min-width: 0px;`);

      // Keep a reference to the component instance
      this.componentRefs.push(componentRef.instance);
    });
  }

  private updateComponentsWithDataSets() {
    if (!this.componentRefs || this.componentRefs.length === 0) {
      // If components are not yet created, generate them
      this.generateElements();
      return;
    }

    // Use the selected configuration if available, otherwise fallback.
    const config = this.selectedConfiguration || this.panelConfig.configurations[0];

    config.elements.forEach((element, index) => {
      const componentInstance = this.componentRefs[index];
      if (!componentInstance)
        return;
      const dataSetsToFind = element.settings.dataSets ?? [element.settings.dataSet];
      const datasets = this._dataSets.filter(ds => dataSetsToFind.includes(ds.name));
      componentInstance.dataSets = datasets;


      if (datasets.length === 0) {
        console.warn(`No datasets found for element at index ${index} and name ${element.name}`);
      }
    });
  }
}
